// Write off part of a claim's balance. Opened from the ledger detail panel.
//
// CLAUDE.md principle 10 shapes every decision here:
//   - the modal starts CLOSED and EMPTY, and is only mounted when the biller opens
//     it. Nothing is pre-filled with an amount;
//   - focus goes to the amount field, never to the confirm button, so Enter on a
//     freshly opened form cannot post anything;
//   - the button says what it will do and how much, not "Submit";
//   - the reason decides which balance is forgiven, and the form SAYS which, before
//     the click, because the server will not cross to the other party and the biller
//     needs to know that in advance rather than from the shortfall message after.

import React, { useEffect, useRef, useState } from "react";
import "./PostingModals.css";
import { formatCurrency } from "../../utils/formatCurrency";

const API_URL = process.env.REACT_APP_API_URL || "";

// Mirrors allocation_service.WRITEOFF_REASONS and _REASON_PARTY. Kept in the same
// order as the service's map so the two read as one list.
const REASONS = [
  { key: "contractual", label: "Contractual", party: "insurance" },
  { key: "timely_filing", label: "Timely filing", party: "insurance" },
  { key: "small_balance", label: "Small balance", party: "patient" },
  { key: "bad_debt", label: "Bad debt", party: "patient" },
  { key: "courtesy", label: "Courtesy", party: "patient" },
  { key: "other", label: "Other", party: "insurance" },
];

const PARTY_LABEL = { insurance: "the payer", patient: "the patient" };

export default function WriteoffModal({
  practiceId,
  claim,
  onClose,
  onPosted,
}) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [party, setParty] = useState("");
  const [note, setNote] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);
  const amountRef = useRef(null);

  useEffect(() => {
    // The amount field, not the confirm button. A money form whose default focus is
    // the action is one keystroke from a posting nobody intended.
    amountRef.current?.focus();
  }, []);

  const chosen = REASONS.find((r) => r.key === reason);
  const effectiveParty = party || chosen?.party || null;
  const parsed = Number(amount);
  const valid =
    reason && amount !== "" && Number.isFinite(parsed) && parsed > 0;

  const submit = async () => {
    if (!valid || posting) return;
    setPosting(true);
    setError(null);
    try {
      const resp = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims/${claim.id}/writeoff`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount,
            reason,
            note: note || undefined,
            party: party || undefined,
          }),
        },
      );
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(
          body.error || `Could not post the write-off (${resp.status})`,
        );
      }
      onPosted(body);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="posting-modal-backdrop" onClick={onClose}>
      <div
        className="posting-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Write off part of this claim"
      >
        <div className="posting-modal-header">
          <h2>Write off a balance</h2>
          <button
            className="posting-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="posting-modal-body">
          <div className="posting-claim-summary">
            <span>
              Claim <strong>#{claim.id}</strong>
            </span>
            <span>{claim.patient_name || "—"}</span>
            <span>{claim.date_of_service || "—"}</span>
            <span>
              Billed <strong>{formatCurrency(claim.billed_amount)}</strong>
            </span>
          </div>

          <p className="posting-modal-lede">
            A write-off records that this money will not be collected. It is
            posted to the ledger and can be reversed by a compensating posting —
            never deleted.
          </p>

          <label className="posting-field">
            <span>Amount</span>
            <input
              ref={amountRef}
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              placeholder="0.00"
              disabled={posting}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>

          <label className="posting-field">
            <span>Reason</span>
            <select
              value={reason}
              disabled={posting}
              onChange={(e) => setReason(e.target.value)}
            >
              <option value="">Choose a reason…</option>
              {REASONS.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>

          {chosen && (
            <div className="posting-party-note">
              This forgives what <strong>{PARTY_LABEL[effectiveParty]}</strong>{" "}
              owes.
              {chosen.key === "other" && (
                <>
                  {" "}
                  <label className="posting-inline-select">
                    Change to
                    <select
                      value={party || 'insurance'}
                      disabled={posting}
                      onChange={(e) => setParty(e.target.value)}
                    >
                      <option value="insurance">the payer</option>
                      <option value="patient">the patient</option>
                    </select>
                  </label>
                </>
              )}
              <div className="posting-party-warning">
                It will not cross to the other side. If less is open than you
                enter, Atlas posts what is open and tells you what it did not
                write off.
              </div>
            </div>
          )}

          <label className="posting-field">
            <span>
              Note <em>(optional)</em>
            </span>
            <input
              type="text"
              value={note}
              maxLength={500}
              placeholder="anything the next person should know"
              disabled={posting}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>

          {error && <div className="posting-error">{error}</div>}
        </div>

        <div className="posting-modal-footer">
          <button
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            disabled={posting}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={submit}
            disabled={!valid || posting}
          >
            {posting
              ? "Posting…"
              : valid
                ? `Write off ${formatCurrency(parsed)}`
                : "Write off"}
          </button>
        </div>
      </div>
    </div>
  );
}
